/* eslint-disable @typescript-eslint/no-explicit-any, no-empty */
import { deleteField, doc, getDoc, getDocFromServer, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { db, getAuthInstance } from "@/lib/firebase";
import type { DashboardView, WidgetCustomizationState, DashboardLayoutState, ViewModeState, DashboardMeta } from './dashboardTypes';

type DashboardDoc = DashboardMeta & {
  layouts?: Record<string, DashboardLayoutState>;
  viewModes?: Record<string, ViewModeState>;
  globalWidgetCustomizations?: Record<string, WidgetCustomizationState>;
  updatedAt?: string;
};

const DASHBOARD_COLLECTION = "dashboardState";
const DASHBOARD_DOC_ID = "shared-dashboard";

// Cache current user ID for synchronous access
let cachedUserId: string | null = null;
let authChangeListeners: Array<() => void> = [];
let dashboardRemoteWritable = true;
let dashboardRemoteWarned = false;
let authReadyResolved = false;
let resolveAuthReady: (() => void) | null = null;
let authReadyPromise: Promise<void> | null = null;
let authTrackingPromise: Promise<void> | null = null;

const isPermissionError = (error: unknown): boolean => {
  const message = String(error ?? '').toLowerCase();
  return (
    message.includes('permission') ||
    message.includes('insufficient') ||
    message.includes('missing or insufficient permissions')
  );
};

const markDashboardRemoteUnavailable = (reason: unknown) => {
  dashboardRemoteWritable = false;
  if (dashboardRemoteWarned) return;
  dashboardRemoteWarned = true;
  try {
    console.warn('[dashboardStore] Firestore denied for this operation:', reason);
  } catch {
    // ignore logging errors
  }
};

const notifyAuthChangeListeners = () => {
  authChangeListeners.forEach(listener => {
    try { listener(); } catch (err) {
      console.error('[dashboardStore] Auth change listener error:', err);
    }
  });
};

const markAuthReady = () => {
  if (authReadyResolved) return;
  authReadyResolved = true;
  resolveAuthReady?.();
  resolveAuthReady = null;
};

const ensureAuthTracking = async (): Promise<void> => {
  if (authTrackingPromise) {
    await authTrackingPromise;
    return;
  }

  if (!authReadyPromise) {
    authReadyPromise = new Promise<void>((resolve) => {
      resolveAuthReady = resolve;
    });
  }

  authTrackingPromise = (async () => {
    try {
      const auth = await getAuthInstance();
      if (!auth) {
        markAuthReady();
        return;
      }

      if (auth.currentUser?.uid) {
        cachedUserId = auth.currentUser.uid;
        console.log('[dashboardStore] Cached user ID set from currentUser:', cachedUserId);
        markAuthReady();
      }

      const { onAuthStateChanged } = await import('firebase/auth');
      onAuthStateChanged(
        auth,
        (user) => {
          const oldId = cachedUserId;
          cachedUserId = user?.uid || null;
          markAuthReady();
          if (oldId !== cachedUserId) {
            console.log('[dashboardStore] User ID changed:', { from: oldId, to: cachedUserId });
            notifyAuthChangeListeners();
          }
        },
        (err) => {
          console.error('[dashboardStore] Auth listener error:', err);
          markAuthReady();
        }
      );
    } catch (err) {
      console.error('[dashboardStore] Failed to initialize auth listener:', err);
      markAuthReady();
    }
  })();

  await authTrackingPromise;
};

const waitForInitialAuthReady = async (timeoutMs = 10000): Promise<void> => {
  await ensureAuthTracking();
  if (authReadyResolved) return;
  await Promise.race([
    authReadyPromise ?? Promise.resolve(),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
  if (authReadyResolved) return;
  try {
    const auth = await getAuthInstance();
    cachedUserId = auth?.currentUser?.uid ?? null;
  } catch {
    cachedUserId = null;
  }
  markAuthReady();
};

void ensureAuthTracking();

// Export function to force refresh cached user ID (call this after sign-in)
export async function refreshCachedUserId(): Promise<void> {
  try {
    await ensureAuthTracking();
    const auth = await getAuthInstance();
    const oldId = cachedUserId;
    cachedUserId = auth?.currentUser?.uid || null;
    console.log('[dashboardStore] refreshCachedUserId:', { from: oldId, to: cachedUserId });

    // Trigger re-subscription if user ID actually changed
    if (oldId !== cachedUserId) {
      console.log('[dashboardStore] User ID changed, notifying listeners');
      notifyAuthChangeListeners();
    }
  } catch (err) {
    console.error('[dashboardStore] refreshCachedUserId failed:', err);
  }
}

export const sanitizeDashboardViews = (views: DashboardView[] | undefined): DashboardView[] => {
  if (!Array.isArray(views)) return [];
  return views
    .map((view) => {
      if (!view || typeof view !== 'object') return null;
      if (typeof view.id !== 'string' || typeof view.name !== 'string') return null;
      const widgetIds = Array.isArray(view.widgetIds)
        ? view.widgetIds.filter((widgetId) => typeof widgetId === 'string')
        : [];
      return { id: view.id, name: view.name, widgetIds };
    })
    .filter((view): view is DashboardView => Boolean(view));
};

const sanitizeDashboardMeta = (meta: DashboardMeta | null): DashboardMeta | null => {
  if (!meta) return null;
  const views = sanitizeDashboardViews(meta.views);
  const nextActiveViewId = views.some((view) => view.id === meta.activeViewId)
    ? meta.activeViewId
    : views[0]?.id;

  const developerPageAccess =
    meta.developerPageAccess === 'passcode' ||
    meta.developerPageAccess === 'admins' ||
    meta.developerPageAccess === 'disabled'
      ? meta.developerPageAccess
      : undefined;

  return {
    ...meta,
    views,
    activeViewId: nextActiveViewId,
    developerPageAccess,
    betaDashboardsVisible: typeof meta.betaDashboardsVisible === 'boolean' ? meta.betaDashboardsVisible : undefined,
  };
};

const getDashboardDocRef = () => {
  if (!db) {
    console.error('[dashboardStore] ⚠️ Firestore db is NULL - cannot save to Firestore!');
    throw new Error("Firestore is not configured.");
  }
  if (!cachedUserId) {
    // Guests read from shared-dashboard
    return doc(db, DASHBOARD_COLLECTION, DASHBOARD_DOC_ID);
  }
  const docId = `user-${cachedUserId}`;
  console.log('[dashboardStore] getDashboardDocRef:', { docId, hasCachedUser: !!cachedUserId });
  return doc(db, DASHBOARD_COLLECTION, docId);
};

const getSharedDashboardDocRef = () => {
  if (!db) {
    throw new Error("Firestore is not configured.");
  }
  return doc(db, DASHBOARD_COLLECTION, DASHBOARD_DOC_ID);
};

const emitIntent = (_name: string, _detail?: any) => {
  // diagnostics removed
};

const ensureCachedUserId = async (timeoutMs = 10000): Promise<string | null> => {
  if (cachedUserId) return cachedUserId;
  try {
    await waitForInitialAuthReady(timeoutMs);
    const auth = await getAuthInstance();
    if (!auth) return null;
    if (auth.currentUser?.uid) {
      cachedUserId = auth.currentUser.uid;
      return cachedUserId;
    }
    cachedUserId = null;
    return null;
  } catch {
    return null;
  }
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs = 10000): Promise<T> =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Firestore request timed out."));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

const getDashboardDocRefAsync = async () => {
  await ensureCachedUserId();
  return getDashboardDocRef();
};

const getDashboardSnapshot = async () => {
  const docRef = await getDashboardDocRefAsync();
  if (!docRef) return null;
  try {
    return await withTimeout(getDocFromServer(docRef));
  } catch {
    return await withTimeout(getDoc(docRef));
  }
};

const stripUndefined = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item)) as T;
  }
  if (value && typeof value === "object") {
    const cleaned = Object.fromEntries(
      Object.entries(value).flatMap(([key, val]) =>
        val === undefined ? [] : [[key, stripUndefined(val)]]
      )
    );
    return cleaned as T;
  }
  return value;
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] dashboardStore', detail);
  } catch {
    // ignore logging errors
  }
};

export async function loadDashboardMeta(): Promise<DashboardMeta | null> {
  if (!db) {
    return null;
  }
  try {
    const snapshot = await getDashboardSnapshot();
    if (snapshot?.exists()) {
      const data = snapshot.data() as DashboardDoc;
      const meta = {
        views: data.views ?? [],
        activeViewId: data.activeViewId,
        betaEnabled: data.betaEnabled,
        betaDashboardsVisible: (data as any).betaDashboardsVisible,
        developerPageAccess: (data as any).developerPageAccess,
      };
      return sanitizeDashboardMeta(meta);
    }

    // MIGRATION: If user doc doesn't exist or is empty, try loading from shared-dashboard
    if (cachedUserId) {
      console.log('[dashboardStore] User doc empty, checking shared-dashboard for migration...');
      const sharedDocRef = getSharedDashboardDocRef();
      const sharedSnapshot = await getDoc(sharedDocRef);
      if (sharedSnapshot.exists()) {
        const sharedData = sharedSnapshot.data() as DashboardDoc;
        if (sharedData.views && sharedData.views.length > 0) {
          console.log('[dashboardStore] Found data in shared-dashboard, migrating to user doc...');
          const meta = {
            views: sharedData.views,
            activeViewId: sharedData.activeViewId,
            betaEnabled: sharedData.betaEnabled,
            betaDashboardsVisible: (sharedData as any).betaDashboardsVisible,
            developerPageAccess: (sharedData as any).developerPageAccess,
          };
          // Save to user doc
          await saveDashboardMeta(meta);
          console.log('[dashboardStore] Migration complete!');
          return sanitizeDashboardMeta(meta);
        }
      }
    }
  } catch (err) {
    console.error('[dashboardStore] loadDashboardMeta error:', err);
    return null;
  }
  return null;
}

export async function saveDashboardMeta(meta: Partial<DashboardMeta>): Promise<void> {
  console.log('[dashboardStore] saveDashboardMeta called:', { meta, hasDb: !!db, cachedUserId });
  const docRef = await getDashboardDocRefAsync();
  if (!docRef) {
    console.error('[dashboardStore] No docRef returned - skipping save');
    return;
  }
  const sanitizedMeta = stripUndefined(meta);
  logFirestoreWrite({ doc: docRef.path, action: 'saveMeta' });
  console.log('[dashboardStore] Saving to Firestore:', docRef.path, sanitizedMeta);
  try {
    await setDoc(docRef, { ...sanitizedMeta }, { merge: true });
    console.log('[dashboardStore] ✓ Saved to Firestore successfully');
  } catch (error) {
    if (isPermissionError(error)) {
      markDashboardRemoteUnavailable(error);
    }
    console.error('[dashboardStore] saveDashboardMeta Firestore failed:', error);
  }
  try { emitIntent('saveDashboardMeta', { meta: sanitizedMeta }); } catch {}
  // Mirror to shared-dashboard so guests see Paul's latest views
  if (cachedUserId && db) {
    try {
      const sharedRef = getSharedDashboardDocRef();
      await setDoc(sharedRef, { ...sanitizedMeta }, { merge: true });
    } catch { /* ignore mirror failures */ }
  }
}

// Save widget customizations globally for all users (logged-out and new)
export async function saveGlobalWidgetCustomizations(customizations: Record<string, WidgetCustomizationState>): Promise<void> {
  if (!db) {
    console.warn('[dashboardStore] Firestore not initialized for global customizations save');
    return;
  }
  try {
    const docRef = doc(db, DASHBOARD_COLLECTION, DASHBOARD_DOC_ID);
    await setDoc(docRef, { globalWidgetCustomizations: customizations }, { merge: true });
    console.log('[dashboardStore] ✓ Saved global widget customizations');
  } catch (error) {
    console.error('[dashboardStore] Failed to save global widget customizations:', error);
  }
}

export async function loadDashboardLayout(storageKey: string): Promise<DashboardLayoutState | null> {
  if (!db) {
    return null;
  }
  try {
    const snapshot = await getDashboardSnapshot();
    if (snapshot?.exists()) {
      const data = snapshot.data() as DashboardDoc;
      const layout = data.layouts?.[storageKey];
      if (layout) {
        // Merge globalWidgetCustomizations when logged out so signed-out users
        // see the exact widget state Paul left (cyclerow selector, etc.)
        if (!cachedUserId && data.globalWidgetCustomizations) {
          return {
            ...layout,
            customizations: {
              ...data.globalWidgetCustomizations,
              ...layout.customizations,
            },
          };
        }
        return layout;
      }
    }

    // MIGRATION: Check shared-dashboard for old layouts
    if (cachedUserId) {
      const sharedDocRef = getSharedDashboardDocRef();
      const sharedSnapshot = await getDoc(sharedDocRef);
      if (sharedSnapshot.exists()) {
        const sharedData = sharedSnapshot.data() as DashboardDoc;
        const sharedLayout = sharedData.layouts?.[storageKey];
        if (sharedLayout) {
          console.log('[dashboardStore] Found layout in shared-dashboard, migrating:', storageKey);
          // Migrate to user doc
          await saveDashboardLayout(storageKey, sharedLayout);
          return sharedLayout;
        }
      }
    }
  } catch (err) {
    if (isPermissionError(err)) {
      markDashboardRemoteUnavailable(err);
      return null;
    }
    console.error('[dashboardStore] loadDashboardLayout error:', err);
    return null;
  }
  return null;
}

export async function saveDashboardLayout(
  storageKey: string,
  layoutState: DashboardLayoutState
): Promise<void> {
  console.log('[dashboardStore] saveDashboardLayout called:', { storageKey, layoutLength: layoutState.layout?.length });
  const docRef = await getDashboardDocRefAsync();
  if (!docRef) {
    // Mobile/unauthenticated sessions may not have a user dashboard doc yet.
    // Local state is already updated; skip remote write without throwing.
    console.warn('[dashboardStore] No docRef available, skipping remote layout save');
    return;
  }
  console.log('[dashboardStore] DocRef obtained:', docRef.path);
  const sanitizedLayoutState = stripUndefined(layoutState);
  try {
    logFirestoreWrite({ doc: docRef.path, action: 'updateLayout', storageKey });
    console.log('[dashboardStore] Attempting updateDoc...');
    await updateDoc(docRef, {
      [`layouts.${storageKey}`]: sanitizedLayoutState,
    });
    console.log('[dashboardStore] ✓ updateDoc succeeded for', storageKey);
    try { emitIntent('saveDashboardLayout_update', { storageKey }); } catch {}
  } catch (updateErr) {
    if (isPermissionError(updateErr)) {
      markDashboardRemoteUnavailable(updateErr);
      throw updateErr instanceof Error ? updateErr : new Error(String(updateErr));
    }
    console.log('[dashboardStore] updateDoc failed, trying setDoc:', updateErr);
    try {
      logFirestoreWrite({ doc: docRef.path, action: 'setLayout', storageKey });
      await setDoc(docRef, { layouts: { [storageKey]: sanitizedLayoutState } }, { merge: true });
      console.log('[dashboardStore] ✓ setDoc succeeded for', storageKey);
      try { emitIntent('saveDashboardLayout_setdoc', { storageKey }); } catch {}
    } catch (setErr) {
      if (isPermissionError(setErr)) {
        markDashboardRemoteUnavailable(setErr);
      }
      console.error('[dashboardStore] ✗ Both updateDoc and setDoc failed:', setErr);
      throw setErr instanceof Error ? setErr : new Error(String(setErr));
    }
  }
  // Mirror layout to shared-dashboard so guests see Paul's current state
  if (cachedUserId && db) {
    try {
      const sharedRef = getSharedDashboardDocRef();
      await setDoc(sharedRef, { layouts: { [storageKey]: sanitizedLayoutState } }, { merge: true });
    } catch { /* ignore mirror failures */ }
  }
}

export async function deleteDashboardLayout(storageKey: string): Promise<void> {
  const docRef = await getDashboardDocRefAsync();
  if (!docRef) {
    return;
  }
  logFirestoreWrite({ doc: docRef.path, action: 'deleteLayout', storageKey });
  await setDoc(docRef, { layouts: { [storageKey]: deleteField() } }, { merge: true });
  try { emitIntent('deleteDashboardLayout', { storageKey }); } catch {}
}

export function subscribeDashboardMeta(
  onData: (meta: DashboardMeta | null) => void,
  onError?: (error: Error) => void
): (() => void) | null {
  let unsubscribe: (() => void) | null = null;
  let isActive = true;

  const setupSubscription = async () => {
    // Clean up existing subscription if any
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }

    if (!isActive) return;

    try {
      await waitForInitialAuthReady();
      console.log('[dashboardStore] setupSubscription: Ready with cachedUserId:', cachedUserId);
    } catch (err) {
      console.error('[dashboardStore] setupSubscription: Failed to check auth:', err);
    }

    const docRef = getDashboardDocRef();
    console.log('[dashboardStore] setupSubscription: docRef path will be:', docRef?.path || 'null');
    if (!docRef) {
      onData(null);
      return;
    }

    console.log('[dashboardStore] Setting up subscription to:', docRef.path);

    unsubscribe = onSnapshot(
      docRef,
      async (snapshot) => {
        console.log('[dashboardStore] Subscription fired - doc exists:', snapshot.exists(), 'path:', docRef.path);

        if (!snapshot.exists()) {
          // MIGRATION: Try loading from shared-dashboard
          if (cachedUserId && db) {
            console.log('[dashboardStore] Subscription: User doc missing, checking shared-dashboard...');
            const sharedDocRef = getSharedDashboardDocRef();
            try {
              const sharedSnapshot = await getDoc(sharedDocRef);
              console.log('[dashboardStore] Shared-dashboard exists:', sharedSnapshot.exists());
              if (sharedSnapshot.exists()) {
                const sharedData = sharedSnapshot.data() as DashboardDoc;
                console.log('[dashboardStore] Shared-dashboard data:', {
                  hasViews: !!sharedData.views,
                  viewCount: sharedData.views?.length || 0,
                  views: sharedData.views
                });
                if (sharedData.views && sharedData.views.length > 0) {
                  console.log('[dashboardStore] Subscription: Migrating from shared-dashboard...');
                  const meta = {
                    views: sharedData.views,
                    activeViewId: sharedData.activeViewId,
                    betaEnabled: sharedData.betaEnabled,
                    betaDashboardsVisible: (sharedData as any).betaDashboardsVisible,
                    developerPageAccess: (sharedData as any).developerPageAccess,
                  };
                  await saveDashboardMeta(meta);
                  onData(sanitizeDashboardMeta(meta));
                  return;
                }
              }
            } catch (err) {
              console.error('[dashboardStore] Migration failed:', err);
            }
          }
          onData(null);
          return;
        }
        const data = snapshot.data() as DashboardDoc;
      const views = data.views ?? [];
      console.log('[dashboardStore] User doc data:', {
        hasViews: !!views,
        viewCount: views.length,
        views: views
      });

      // MIGRATION: If user doc exists but has no views, check shared-dashboard
      if (views.length === 0 && cachedUserId && db) {
        console.log('[dashboardStore] Subscription: User doc has no views, checking shared-dashboard...');
        const sharedDocRef = getSharedDashboardDocRef();
        try {
          const sharedSnapshot = await getDoc(sharedDocRef);
          console.log('[dashboardStore] Shared-dashboard exists:', sharedSnapshot.exists());
          if (sharedSnapshot.exists()) {
            const sharedData = sharedSnapshot.data() as DashboardDoc;
            console.log('[dashboardStore] Shared-dashboard data:', {
              hasViews: !!sharedData.views,
              viewCount: sharedData.views?.length || 0,
              views: sharedData.views
            });
            if (sharedData.views && sharedData.views.length > 0) {
              console.log('[dashboardStore] Subscription: Found views in shared-dashboard, migrating...');
              const meta = {
                views: sharedData.views,
                activeViewId: sharedData.activeViewId,
                betaEnabled: data.betaEnabled ?? sharedData.betaEnabled,
                betaDashboardsVisible: (data as any).betaDashboardsVisible ?? (sharedData as any).betaDashboardsVisible,
                developerPageAccess: (data as any).developerPageAccess ?? (sharedData as any).developerPageAccess,
              };
              await saveDashboardMeta(meta);
              onData(sanitizeDashboardMeta(meta));
              return;
            }
          }
        } catch (err) {
          console.error('[dashboardStore] Migration check failed:', err);
        }
      }

      const meta = {
        views,
        activeViewId: data.activeViewId,
        betaEnabled: data.betaEnabled,
        betaDashboardsVisible: (data as any).betaDashboardsVisible,
        developerPageAccess: (data as any).developerPageAccess,
      };
      console.log('[dashboardStore] About to call onData with meta:', { viewCount: meta.views?.length, activeViewId: meta.activeViewId });
      const sanitized = sanitizeDashboardMeta(meta);
      console.log('[dashboardStore] Sanitized meta:', { viewCount: sanitized?.views?.length, activeViewId: sanitized?.activeViewId });
      onData(sanitized);
    },
    (error) => {
      if (isPermissionError(error)) {
        markDashboardRemoteUnavailable(error);
        onData(null);
        return;
      }
      onData(null);
      onError?.(error as Error);
    }
  );
  };

  // Setup initial subscription
  setupSubscription();

  // Re-subscribe when user ID changes
  const authChangeHandler = () => {
    console.log('[dashboardStore] Auth changed, re-subscribing to dashboard meta');
    setupSubscription();
  };
  authChangeListeners.push(authChangeHandler);

  // Return cleanup function
  return () => {
    isActive = false;
    if (unsubscribe) {
      unsubscribe();
    }
    // Remove from auth change listeners
    const index = authChangeListeners.indexOf(authChangeHandler);
    if (index > -1) {
      authChangeListeners.splice(index, 1);
    }
  };
}

export function subscribeDashboardLayout(
  storageKey: string,
  onData: (layout: DashboardLayoutState | null) => void,
  onError?: (error: Error) => void
): (() => void) | null {
  let unsubscribe: (() => void) | null = null;
  let isActive = true;

  const setupSubscription = async () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    if (!isActive) return;

    const docRef = await getDashboardDocRefAsync();
    if (!docRef) {
      onData(null);
      return;
    }

    unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          onData(null);
          return;
        }
        const data = snapshot.data() as DashboardDoc;
        const layout = data.layouts?.[storageKey] ?? null;
        if (layout && !cachedUserId && data.globalWidgetCustomizations) {
          onData({
            ...layout,
            customizations: {
              ...data.globalWidgetCustomizations,
              ...layout.customizations,
            },
          });
        } else {
          onData(layout);
        }
      },
      (error) => {
        if (isPermissionError(error)) {
          markDashboardRemoteUnavailable(error);
          onData(null);
          return;
        }
        onData(null);
        onError?.(error as Error);
      }
    );
  };

  void setupSubscription();

  const authChangeHandler = () => {
    void setupSubscription();
  };
  authChangeListeners.push(authChangeHandler);

  return () => {
    isActive = false;
    unsubscribe?.();
    const index = authChangeListeners.indexOf(authChangeHandler);
    if (index > -1) {
      authChangeListeners.splice(index, 1);
    }
  };
}
// View Mode & Hidden Widgets (Firestore)
export async function loadViewMode(storageKey: string): Promise<ViewModeState | null> {
  if (!dashboardRemoteWritable) {
    return null;
  }
  const docRef = await getDashboardDocRefAsync();
  if (!docRef) {
    return null;
  }
  try {
    const snapshot = await getDashboardSnapshot();
    if (snapshot && snapshot.exists()) {
      const data = snapshot.data() as DashboardDoc;
      return data.viewModes?.[storageKey] ?? null;
    }
  } catch {
    return null;
  }
  return null;
}

export async function saveViewMode(storageKey: string, viewModeState: ViewModeState): Promise<void> {
  if (!dashboardRemoteWritable) {
    return;
  }
  const docRef = await getDashboardDocRefAsync();
  if (!docRef) {
    throw new Error('No dashboard document reference available for view mode save.');
  }
  const sanitized = stripUndefined(viewModeState);
  logFirestoreWrite({ doc: docRef.path, action: 'updateViewMode', storageKey });
  try {
    await updateDoc(docRef, {
      [`viewModes.${storageKey}`]: sanitized,
    });
  } catch {
    try {
      await setDoc(docRef, { viewModes: { [storageKey]: sanitized } }, { merge: true });
    } catch (error) {
      if (isPermissionError(error)) {
        markDashboardRemoteUnavailable(error);
        throw error instanceof Error ? error : new Error(String(error));
      }
      throw error;
    }
  }
}

export function subscribeViewMode(
  storageKey: string,
  onData: (viewMode: ViewModeState | null) => void,
  onError?: (error: Error) => void
): (() => void) | null {
  let unsubscribe: (() => void) | null = null;
  let isActive = true;

  const setupSubscription = async () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    if (!isActive) return;

    const docRef = await getDashboardDocRefAsync();
    if (!docRef) {
      onData(null);
      return;
    }

    unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          onData(null);
          return;
        }
        const data = snapshot.data() as DashboardDoc;
        onData(data.viewModes?.[storageKey] ?? null);
      },
      (error) => {
        if (isPermissionError(error)) {
          markDashboardRemoteUnavailable(error);
          onData(null);
          return;
        }
        onError?.(error as Error);
      }
    );
  };

  void setupSubscription();

  const authChangeHandler = () => {
    void setupSubscription();
  };
  authChangeListeners.push(authChangeHandler);

  return () => {
    isActive = false;
    unsubscribe?.();
    const index = authChangeListeners.indexOf(authChangeHandler);
    if (index > -1) {
      authChangeListeners.splice(index, 1);
    }
  };
}
